import React, { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Dialog,
    Grid,
    IconButton,
    Slider,
    Tooltip,
    Typography,
} from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import VerifiedIcon from '@mui/icons-material/Verified';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import Cropper from 'react-easy-crop';
import { useDispatch } from 'react-redux';
import type { IInfluencer } from '../../redux/buzzbridge/types';
import { getPublicInfluencerProfile } from '../../redux/buzzbridge/actions';
import { FONT_ENUM } from '../../types/global/fonts.types';
import {
    BRAND_LUXE_BG,
    BRAND_LUXE_PRIMARY,
    BRAND_LUXE_SECONDARY,
    BRAND_SURFACE_CONTAINER,
    BRAND_SURFACE_CONTAINER_HIGHEST,
    BRAND_SURFACE_CONTAINER_LOW,
    brandMeshBackground,
} from '../../pages/BrandDashboard/brandDashboardTokens';
import { PUBLIC_CREATOR_COLLAB_BAR_SPACER } from './PublicCreatorCollabBar';
import { InstagramGlyph, TiktokGlyph, YoutubeGlyph } from '../BuzzBridge/SocialPlatformSvgs';
import { isCreatorVerified, influencerProfilePhotoCoverSx } from '../BuzzBridge/utils/creatorProfileHelpers';
import { formatSocialHandle } from '../../utils/socialHandle';
import {
    accountHandleLabel,
    creatorPrimaryCategoryLabel,
    formatReachShort,
    formatUsd,
    heroImageUrl,
    platformRateCardsFromInfluencer,
    publicEngagementLabel,
    publicTotalReach,
} from './publicInfluencerProfileUtils';
import { InfluencerPublicReviewsSection, publicRatingLabel } from './InfluencerPublicReviewsSection';
import { PublicInfluencerPortfolioSections } from './PublicInfluencerPortfolioSections';
import { RecentSocialPosts } from '../Influencer/RecentSocialPosts';
import { getJWtDetails, showToastMessage } from '../../utils/AppUtils';
import { updateProfileUrl, influencersUrl } from '../../configurations/api/url';

const ON_VARIANT = '#adaaaa';
/** Tailwind max-w-7xl — full-bleed page with readable content width */
const CONTENT_MAX_WIDTH = 1280;

const sectionPx = { xs: 2, sm: 3, md: 4 };

function ProfileContentShell({ children, sx, id }: { children: ReactNode; sx?: object; id?: string }) {
    return (
        <Box id={id} sx={{ width: '100%', maxWidth: CONTENT_MAX_WIDTH, mx: 'auto', px: sectionPx, ...sx }}>
            {children}
        </Box>
    );
}

export interface PublicInfluencerProfileFooterAction {
    label: string;
    onClick: () => void;
    disabled?: boolean;
    invited?: boolean;
}

export interface PublicInfluencerProfileViewProps {
    influencer: IInfluencer;
    /** Sticky footer CTA (e.g. invite in brand drawer) */
    footerAction?: PublicInfluencerProfileFooterAction;
    /** Secondary link under hero (public share page) */
    shareUrl?: string;
    /** Drawer uses a shorter hero; public page uses cinematic height */
    layout?: 'page' | 'drawer';
    /** Called when the creator name is clicked — caller provides the resolved URL */
    onNameClick?: () => void;
    /** Called when the chat icon is clicked — caller handles auth gate + dialogue */
    onMessage?: () => void;
    /** When true, shows the cover position edit button (only for the profile owner) */
    isOwner?: boolean;
}

function PlatformGlyph({ platform }: { platform: 'instagram' | 'tiktok' | 'youtube' }) {
    const sx = { width: 28, height: 28 };
    if (platform === 'instagram') return <InstagramGlyph sx={sx} />;
    if (platform === 'tiktok') return <TiktokGlyph sx={sx} />;
    return <YoutubeGlyph sx={sx} />;
}

export function PublicInfluencerProfileView({
    influencer,
    footerAction,
    shareUrl,
    layout = 'page',
    onNameClick,
    onMessage,
    isOwner = false,
}: PublicInfluencerProfileViewProps) {
    const isPage = layout === 'page';
    const dispatch = useDispatch();
    const hero = heroImageUrl(influencer);
    const [heroSrc, setHeroSrc] = useState(hero);
    const [cropOpen, setCropOpen] = useState(false);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState<any>(null);
    const [saving, setSaving] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [cropSrc, setCropSrc] = useState<string | null>(null);

    useEffect(() => {
        setHeroSrc(hero);
    }, [hero]);

    const onCropComplete = useCallback((_: any, areaPixels: any) => {
        setCroppedAreaPixels(areaPixels);
    }, []);

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const url = URL.createObjectURL(file);
        setCropSrc(url);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCropOpen(true);
        // reset input so same file can be re-selected
        e.target.value = '';
    };

    const handleSaveCrop = async () => {
        if (!cropSrc || !croppedAreaPixels) return;
        setSaving(true);
        try {
            const { token } = getJWtDetails();

            // Draw cropped area onto a canvas and export as blob
            const image = await new Promise<HTMLImageElement>((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve(img);
                img.onerror = reject;
                img.src = cropSrc;
            });
            const canvas = document.createElement('canvas');
            canvas.width = croppedAreaPixels.width;
            canvas.height = croppedAreaPixels.height;
            const ctx = canvas.getContext('2d')!;
            ctx.drawImage(
                image,
                croppedAreaPixels.x,
                croppedAreaPixels.y,
                croppedAreaPixels.width,
                croppedAreaPixels.height,
                0,
                0,
                croppedAreaPixels.width,
                croppedAreaPixels.height,
            );
            const blob = await new Promise<Blob>((resolve, reject) =>
                canvas.toBlob(b => (b ? resolve(b) : reject(new Error('Canvas empty'))), 'image/jpeg', 0.92),
            );

            const form = new FormData();
            form.append('profile_picture', blob, 'profile.jpg');

            const res = await fetch(updateProfileUrl, {
                method: 'PATCH',
                headers: { Authorization: `Bearer ${token}` },
                body: form,
            });

            if (!res.ok) throw new Error('Upload failed');

            // Also get the uploaded URL from the response and patch the influencer doc
            const resData = await res.json().catch(() => null);
            const uploadedUrl: string | undefined =
                resData?.data?.user?.profile_picture ?? resData?.user?.profile_picture;

            if (influencer._id && uploadedUrl) {
                await fetch(`${influencersUrl}/${influencer._id}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                    body: JSON.stringify({ profile_picture: uploadedUrl }),
                });
            }

            // Update hero preview locally
            const objectUrl = URL.createObjectURL(blob);
            setHeroSrc(objectUrl);
            setCropOpen(false);
            showToastMessage('Profile photo updated successfully', 'success');

            // Invalidate public profile cache
            if (influencer.userName) {
                dispatch(getPublicInfluencerProfile({ userName: influencer.userName }));
            }
        } catch (_) {
            showToastMessage('Failed to update photo. Please try again.', 'error');
        } finally {
            setSaving(false);
        }
    };

    const handle = accountHandleLabel(influencer);
    const verified = isCreatorVerified(influencer);
    const reach = publicTotalReach(influencer);
    const engagement = publicEngagementLabel(influencer);
    const campaigns = influencer.completed_campaigns ?? 0;
    const ratingLabel = publicRatingLabel(influencer);
    const reviewCount = influencer.number_of_reviews ?? influencer.recent_reviews?.length ?? 0;
    const platformCards = platformRateCardsFromInfluencer(influencer);
    const maxPlatformRateRows = Math.max(1, ...platformCards.map(c => (c.rows.length > 0 ? c.rows.length : 1)));
    const categories: Array<{ main: string; sub?: string }> = (influencer.categories ?? [])
        .map(c => (typeof c === 'string' ? { main: c } : { main: c?.main ?? '', sub: c?.sub || undefined }))
        .filter(c => Boolean(c.main?.trim()));
    const categoryChips: Array<{ main: string; sub?: string }> =
        categories.length > 0 ? categories : [{ main: creatorPrimaryCategoryLabel(influencer.categories) }];

    const profileStats: Array<{ label: string; value: string; hint?: string }> = [
        { label: 'Total\nreach', value: formatReachShort(reach) },
        { label: 'Avg\nengagement', value: engagement },
    ];
    if (campaigns > 0) {
        profileStats.push({ label: 'Campaigns\ncompleted', value: String(campaigns) });
    }
    if (ratingLabel !== '—' && reviewCount > 0) {
        profileStats.push({
            label: 'Total\nratings',
            value: ratingLabel,
            hint: `${reviewCount} review${reviewCount === 1 ? '' : 's'}`,
        });
    }
    const statsSm = profileStats.length <= 2 ? 6 : profileStats.length === 3 ? 4 : 3;

    const pageBg = BRAND_LUXE_BG;

    return (
        <Box sx={{ color: '#fff', minHeight: '100%', width: '100%', ...brandMeshBackground(pageBg) }}>
            {/* Hero — full viewport width */}
            <Box
                sx={{
                    position: 'relative',
                    width: '100%',
                    height: isPage ? { xs: 420, md: 530, lg: 618 } : { xs: 320, sm: 380 },
                    overflow: 'hidden',
                }}>
                {heroSrc ? (
                    <Box
                        component="img"
                        src={heroSrc}
                        alt=""
                        onError={() => {
                            const fallback = heroImageUrl(influencer);
                            if (fallback && fallback !== heroSrc) {
                                setHeroSrc(fallback);
                            } else {
                                setHeroSrc(undefined);
                            }
                        }}
                        sx={{
                            position: 'absolute',
                            inset: 0,
                            width: '100%',
                            height: '100%',
                            ...influencerProfilePhotoCoverSx,
                        }}
                    />
                ) : (
                    <Box sx={{ position: 'absolute', inset: 0, bgcolor: BRAND_SURFACE_CONTAINER_HIGHEST }} />
                )}

                {/* Edit photo button — owner only */}
                {isOwner && isPage && (
                    <>
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept="image/*"
                            style={{ display: 'none' }}
                            onChange={handleFileChange}
                        />
                        <Tooltip title="Update profile photo" placement="left">
                            <IconButton
                                onClick={() => fileInputRef.current?.click()}
                                sx={{
                                    position: 'absolute',
                                    top: 16,
                                    right: 16,
                                    zIndex: 10,
                                    bgcolor: 'rgba(0,0,0,0.55)',
                                    backdropFilter: 'blur(8px)',
                                    border: '1px solid rgba(255,255,255,0.15)',
                                    color: '#fff',
                                    '&:hover': { bgcolor: 'rgba(0,0,0,0.75)' },
                                }}>
                                <PhotoCameraIcon sx={{ fontSize: 18 }} />
                            </IconButton>
                        </Tooltip>
                    </>
                )}
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background: `linear-gradient(to top, ${pageBg} 0%, rgba(14,14,14,0.4) 50%, transparent 100%)`,
                        pointerEvents: 'none',
                    }}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        inset: 0,
                        background:
                            'linear-gradient(to right, rgba(14,14,14,0.85) 0%, transparent 55%, transparent 100%)',
                        pointerEvents: 'none',
                    }}
                />
                <ProfileContentShell
                    sx={{
                        position: 'relative',
                        zIndex: 1,
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-end',
                        pb: { xs: 3, md: 5 },
                    }}>
                    {verified && (
                        <Box
                            sx={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: 0.75,
                                alignSelf: 'flex-start',
                                px: 2,
                                py: 0.75,
                                mb: 2,
                                borderRadius: 999,
                                bgcolor: 'rgba(255,201,101,0.1)',
                                border: '1px solid rgba(255,201,101,0.25)',
                                backdropFilter: 'blur(8px)',
                            }}>
                            <VerifiedIcon sx={{ fontSize: 18, color: BRAND_LUXE_PRIMARY }} />
                            <Typography
                                sx={{
                                    fontSize: 11,
                                    fontFamily: FONT_ENUM.MAIN_BOLD,
                                    color: BRAND_LUXE_PRIMARY,
                                    letterSpacing: '0.12em',
                                    textTransform: 'uppercase',
                                }}>
                                Elite creator
                            </Typography>
                        </Box>
                    )}
                    <Box
                        onClick={onNameClick}
                        sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 1,
                            ...(onNameClick && { cursor: 'pointer' }),
                            '&:hover .name-text': onNameClick
                                ? { textDecoration: 'underline', textUnderlineOffset: 4 }
                                : {},
                            '&:hover .name-icon': onNameClick ? { opacity: 1 } : {},
                        }}>
                        <Typography
                            className="name-text"
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: isPage ? { xs: 40, sm: 56, md: 64, lg: 72 } : { xs: 28, sm: 36 },
                                lineHeight: 0.95,
                                letterSpacing: '-0.03em',
                                fontWeight: 900,
                            }}>
                            {influencer.name ?? 'Creator'}
                        </Typography>
                        {onNameClick && (
                            <OpenInNewIcon
                                className="name-icon"
                                sx={{
                                    fontSize: isPage ? { xs: 20, md: 28 } : 18,
                                    color: 'rgba(255,255,255)',
                                    opacity: 1,
                                    transition: 'opacity 0.2s',
                                    flexShrink: 0,
                                    alignSelf: 'flex-end',
                                    mb: 0.2,
                                }}
                            />
                        )}
                    </Box>
                    {handle && (
                        <Typography
                            sx={{
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontSize: isPage ? { xs: 18, md: 22 } : 16,
                                color: BRAND_LUXE_PRIMARY,
                                mt: 1,
                                letterSpacing: '-0.01em',
                            }}>
                            {handle}
                        </Typography>
                    )}
                    {influencer.country && (
                        <Typography sx={{ fontSize: 14, color: ON_VARIANT, mt: 1 }}>{influencer.country}</Typography>
                    )}
                </ProfileContentShell>
                {/* Chat icon — absolute far right of hero, vertically centred */}
                {onMessage && (
                    <IconButton
                        onClick={onMessage}
                        sx={{
                            position: 'absolute',
                            right: { xs: 20, md: 40 },
                            bottom: { xs: 24, md: 40 },
                            zIndex: 2,
                            width: 48,
                            height: 48,
                            bgcolor: 'rgba(0,0,0,0.55)',
                            backdropFilter: 'blur(12px)',
                            border: `1.5px solid ${BRAND_LUXE_PRIMARY}88`,
                            color: BRAND_LUXE_PRIMARY,
                            transition: 'transform 0.2s ease',
                            '&:hover': { transform: 'scale(1.12)', bgcolor: 'rgba(255,201,101,0.15)' },
                        }}>
                        <ChatBubbleOutlineIcon sx={{ fontSize: 22 }} />
                    </IconButton>
                )}
            </Box>

            {/* Stats */}
            <ProfileContentShell sx={{ mt: { xs: -2, md: -3 }, position: 'relative', zIndex: 2 }}>
                <Grid container spacing={{ xs: 1.5, md: 2 }}>
                    {profileStats.map(stat => (
                        <Grid item xs={6} sm={statsSm} key={stat.label}>
                            <Box
                                sx={{
                                    bgcolor: BRAND_SURFACE_CONTAINER_LOW,
                                    borderRadius: 2,
                                    p: { xs: 2, md: 3 },
                                    height: '100%',
                                    transition: 'background-color 0.3s',
                                    '&:hover': { bgcolor: BRAND_SURFACE_CONTAINER },
                                }}>
                                <Typography
                                    sx={{
                                        fontSize: 11,
                                        color: ON_VARIANT,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.14em',
                                        mb: 1,
                                        fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        whiteSpace: 'pre-line',
                                        lineHeight: 1.25,
                                        minHeight: '2.5em',
                                    }}>
                                    {stat.label}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: FONT_ENUM.MAIN_BOLD,
                                        fontSize: { xs: 26, sm: 30, md: 36 },
                                        lineHeight: 1,
                                        color: BRAND_LUXE_PRIMARY,
                                        fontWeight: 800,
                                    }}>
                                    {stat.value}
                                </Typography>
                                {stat.hint ? (
                                    <Typography
                                        sx={{
                                            mt: 0.75,
                                            fontSize: 11,
                                            color: ON_VARIANT,
                                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                                        }}>
                                        {stat.hint}
                                    </Typography>
                                ) : null}
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </ProfileContentShell>

            {/* Categories */}
            <ProfileContentShell sx={{ mt: { xs: 3, md: 4 }, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {categoryChips.map((cat, i) => (
                    <Chip
                        key={i}
                        label={`${cat.main}${cat.sub ? ` · ${cat.sub}` : ''}`}
                        sx={{
                            height: 'auto',
                            py: 0.75,
                            px: 0.5,
                            bgcolor: 'rgba(255,255,255,0.07)',
                            color: '#fff',
                            border: '1px solid rgba(255,255,255,0.1)',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: 12, md: 13 },
                            borderRadius: 999,
                        }}
                    />
                ))}
            </ProfileContentShell>

            {/* Bio */}
            {influencer.bio && (
                <ProfileContentShell sx={{ mt: { xs: 3, md: 4 } }}>
                    <Typography
                        sx={{
                            fontSize: 11,
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            letterSpacing: '0.14em',
                            color: ON_VARIANT,
                            mb: 1.25,
                            textTransform: 'uppercase',
                        }}>
                        Bio
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_MEDIUM,
                            fontSize: { xs: 15, md: 17 },
                            color: 'rgba(255,255,255,0.78)',
                            lineHeight: 1.65,
                            maxWidth: 720,
                        }}>
                        {influencer.bio}
                    </Typography>
                </ProfileContentShell>
            )}

            {/* Ratings & reviews */}
            <ProfileContentShell sx={{ mt: { xs: 3, md: 4 } }}>
                <InfluencerPublicReviewsSection influencer={influencer} compact={!isPage} />
            </ProfileContentShell>

            {/* Rate cards */}
            {!influencer.hide_rate_card && platformCards.length > 0 && (
                <ProfileContentShell id="rate-cards" sx={{ mt: { xs: 6, md: 10 } }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: { xs: 26, md: 30 },
                            mb: 1,
                            letterSpacing: '-0.02em',
                        }}>
                        Rate cards
                    </Typography>
                    <Typography
                        sx={{ fontSize: { xs: 14, md: 16 }, color: ON_VARIANT, mb: { xs: 3, md: 5 }, maxWidth: 480 }}>
                        Premium UGC content tailored for maximum conversion across the major attention economies.
                    </Typography>
                    <Box
                        sx={{
                            display: 'grid',
                            gridTemplateColumns:
                                layout === 'drawer'
                                    ? '1fr'
                                    : {
                                          xs: '1fr',
                                          md: `repeat(${platformCards.length}, minmax(0, 1fr))`,
                                      },
                            gap: { xs: 2, md: 3 },
                            alignItems: 'stretch',
                        }}>
                        {platformCards.map(card => {
                            const profileHref = card.profileLink;
                            return (
                                <Box
                                    key={card.platform}
                                    component={profileHref ? 'a' : 'div'}
                                    href={profileHref ?? undefined}
                                    target={profileHref ? '_blank' : undefined}
                                    rel={profileHref ? 'noopener noreferrer' : undefined}
                                    aria-label={profileHref ? `Open ${card.label} profile in a new tab` : undefined}
                                    sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        height: '100%',
                                        minHeight: layout === 'drawer' ? undefined : { md: 280 },
                                        bgcolor: card.highlighted
                                            ? BRAND_SURFACE_CONTAINER_HIGHEST
                                            : BRAND_SURFACE_CONTAINER,
                                        borderRadius: 2,
                                        p: { xs: 3, md: 4 },
                                        border: card.highlighted
                                            ? '1px solid rgba(255,201,101,0.12)'
                                            : '1px solid rgba(255,255,255,0.05)',
                                        boxShadow: card.highlighted ? '0 16px 48px rgba(0,0,0,0.45)' : 'none',
                                        zIndex: card.highlighted ? 1 : 0,
                                        position: 'relative',
                                        textDecoration: 'none',
                                        color: 'inherit',
                                        cursor: profileHref ? 'pointer' : 'default',
                                        transition: 'border-color 0.15s, transform 0.15s',
                                        '&:hover': profileHref
                                            ? {
                                                  borderColor: card.highlighted
                                                      ? 'rgba(255,201,101,0.3)'
                                                      : 'rgba(255,255,255,0.15)',
                                                  transform: 'translateY(-2px)',
                                              }
                                            : {},
                                    }}>
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'flex-start',
                                            mb: 3,
                                            flexShrink: 0,
                                        }}>
                                        <Box
                                            sx={{
                                                width: 48,
                                                height: 48,
                                                borderRadius: '50%',
                                                bgcolor: card.highlighted
                                                    ? BRAND_LUXE_PRIMARY
                                                    : BRAND_SURFACE_CONTAINER_HIGHEST,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                boxShadow: card.highlighted
                                                    ? '0 0 20px rgba(255,201,101,0.35)'
                                                    : 'none',
                                            }}>
                                            <PlatformGlyph platform={card.platform} />
                                        </Box>
                                        <Box sx={{ textAlign: 'right' }}>
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'flex-end',
                                                    gap: 0.5,
                                                }}>
                                                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 16 }}>
                                                    {card.username ? formatSocialHandle(card.username) : card.label}
                                                </Typography>
                                                {card.linked && (
                                                    <VerifiedIcon sx={{ fontSize: 16, color: BRAND_LUXE_PRIMARY }} />
                                                )}
                                                {profileHref ? (
                                                    <OpenInNewIcon
                                                        sx={{ fontSize: 16, color: ON_VARIANT, opacity: 0.85 }}
                                                    />
                                                ) : null}
                                            </Box>
                                            <Typography sx={{ fontSize: 14, color: ON_VARIANT, mt: 0.25 }}>
                                                {card.followers != null
                                                    ? `${formatReachShort(card.followers)} followers`
                                                    : 'Not linked'}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box
                                        sx={{
                                            flex: 1,
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'flex-start',
                                        }}>
                                        {card.rows.length > 0 ? (
                                            <>
                                                {card.rows.map((row, idx) => (
                                                    <Box
                                                        key={row.label}
                                                        sx={{
                                                            display: 'flex',
                                                            justifyContent: 'space-between',
                                                            alignItems: 'center',
                                                            minHeight: 52,
                                                            py: 1.5,
                                                            borderBottom:
                                                                idx < card.rows.length - 1
                                                                    ? '1px solid rgba(255,255,255,0.06)'
                                                                    : 'none',
                                                        }}>
                                                        <Typography sx={{ fontSize: 15, color: ON_VARIANT }}>
                                                            {row.label}
                                                        </Typography>
                                                        <Typography
                                                            sx={{
                                                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                                                fontSize: 17,
                                                                color: BRAND_LUXE_PRIMARY,
                                                            }}>
                                                            {formatUsd(row.amount)}
                                                        </Typography>
                                                    </Box>
                                                ))}
                                                {Array.from({
                                                    length: maxPlatformRateRows - card.rows.length,
                                                }).map((_, i) => (
                                                    <Box
                                                        key={`rate-spacer-${i}`}
                                                        sx={{ minHeight: 52, flexShrink: 0 }}
                                                        aria-hidden
                                                    />
                                                ))}
                                            </>
                                        ) : (
                                            <Typography sx={{ fontSize: 14, color: ON_VARIANT }}>
                                                Rates on request
                                            </Typography>
                                        )}
                                    </Box>
                                </Box>
                            );
                        })}
                    </Box>
                </ProfileContentShell>
            )}

            <ProfileContentShell sx={{ mt: { xs: 6, md: 10 } }}>
                <RecentSocialPosts influencer={influencer} compact publicView />
            </ProfileContentShell>

            <PublicInfluencerPortfolioSections
                influencer={influencer}
                bottomPadding={footerAction ? 14 : isPage ? PUBLIC_CREATOR_COLLAB_BAR_SPACER : { xs: 4, md: 6 }}
            />

            {shareUrl && (
                <ProfileContentShell sx={{ pb: 4, textAlign: 'center' }}>
                    <Typography
                        sx={{
                            fontSize: 12,
                            color: ON_VARIANT,
                            mb: 0.5,
                            textTransform: 'uppercase',
                            letterSpacing: '0.1em',
                        }}>
                        Share profile
                    </Typography>
                    <Typography sx={{ fontSize: 14, color: BRAND_LUXE_SECONDARY, wordBreak: 'break-all' }}>
                        {shareUrl}
                    </Typography>
                </ProfileContentShell>
            )}

            {footerAction && (
                <Box
                    sx={{
                        position: 'sticky',
                        bottom: 0,
                        left: 0,
                        right: 0,
                        zIndex: 5,
                        py: 2,
                        backgroundImage: `linear-gradient(to top, ${pageBg} 75%, transparent)`,
                    }}>
                    <ProfileContentShell>
                        <Button
                            fullWidth
                            onClick={footerAction.onClick}
                            disabled={footerAction.disabled}
                            endIcon={footerAction.invited ? undefined : <ArrowForwardIcon />}
                            sx={{
                                py: 2,
                                borderRadius: 999,
                                textTransform: 'uppercase',
                                letterSpacing: '0.08em',
                                fontSize: { xs: 14, md: 16 },
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                fontWeight: 800,
                                color: footerAction.invited ? BRAND_LUXE_SECONDARY : '#392700',
                                background: footerAction.invited
                                    ? 'rgba(0,253,193,0.15)'
                                    : `linear-gradient(to top right, ${BRAND_LUXE_PRIMARY}, #feb700)`,
                                border: footerAction.invited ? '1px solid rgba(0,253,193,0.25)' : 'none',
                                boxShadow: footerAction.invited ? 'none' : '0 10px 40px rgba(255,201,101,0.3)',
                                maxWidth: isPage ? 448 : 'none',
                                mx: isPage ? 'auto' : 0,
                                display: 'flex',
                                '&:hover': footerAction.invited
                                    ? { background: 'rgba(0,253,193,0.22)' }
                                    : { transform: 'scale(1.02)', boxShadow: '0 12px 48px rgba(255,201,101,0.4)' },
                            }}>
                            {footerAction.invited ? 'Invited' : footerAction.label}
                        </Button>
                    </ProfileContentShell>
                </Box>
            )}

            {/* Crop dialog — owner only */}
            {isOwner && (
                <Dialog
                    open={cropOpen}
                    onClose={() => !saving && setCropOpen(false)}
                    fullWidth
                    maxWidth="sm"
                    PaperProps={{ sx: { bgcolor: '#111', borderRadius: 3, overflow: 'hidden' } }}>
                    <Box sx={{ position: 'relative', width: '100%', height: 380, bgcolor: '#000' }}>
                        {cropSrc && (
                            <Cropper
                                image={cropSrc}
                                crop={crop}
                                zoom={zoom}
                                aspect={16 / 9}
                                onCropChange={setCrop}
                                onZoomChange={setZoom}
                                onCropComplete={onCropComplete}
                            />
                        )}
                    </Box>
                    <Box sx={{ px: 3, pt: 2, pb: 1 }}>
                        <Typography
                            sx={{
                                fontSize: 11,
                                color: 'rgba(255,255,255,0.4)',
                                mb: 0.5,
                                letterSpacing: '0.08em',
                                textTransform: 'uppercase',
                            }}>
                            Zoom
                        </Typography>
                        <Slider
                            value={zoom}
                            min={1}
                            max={3}
                            step={0.05}
                            onChange={(_, v) => setZoom(v as number)}
                            sx={{ color: BRAND_LUXE_PRIMARY }}
                        />
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, px: 3, pb: 3 }}>
                        <Button
                            onClick={() => setCropOpen(false)}
                            disabled={saving}
                            sx={{ textTransform: 'none', color: 'rgba(255,255,255,0.5)', borderRadius: 999 }}>
                            Cancel
                        </Button>
                        <Button
                            variant="contained"
                            onClick={handleSaveCrop}
                            disabled={saving}
                            sx={{
                                textTransform: 'none',
                                borderRadius: 999,
                                bgcolor: BRAND_LUXE_PRIMARY,
                                color: '#392700',
                                fontFamily: FONT_ENUM.MAIN_BOLD,
                                px: 3,
                                boxShadow: 'none',
                                '&:hover': { bgcolor: '#feb700', boxShadow: 'none' },
                                '&.Mui-disabled': { bgcolor: `${BRAND_LUXE_PRIMARY}50`, color: '#39270060' },
                            }}>
                            {saving ? (
                                <CircularProgress size={16} thickness={5} sx={{ color: 'inherit' }} />
                            ) : (
                                'Save photo'
                            )}
                        </Button>
                    </Box>
                </Dialog>
            )}
        </Box>
    );
}
